import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckboxLabel,
  CheckboxPanel,
  DeleteOnAggregateButton,
  EditModelButton,
  GridLabel,
  HStack,
  Panel,
  RenderIf,
  Snackbar,
  TabPage,
  Tabs,
  TextLabel,
  ToggleButtons,
  UsecaseButton,
} from '@lib/ui-components';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyBranches } from './shared/company-branches/company-branches';
import { ClientInfrastructure } from './client-view/client-infrastructure';
import CompanyAgreements from './shared/company-agreements';
import { ClientData } from './client-view/client-data/client-data';
import { CompanyRole, CompanyViewModel } from '@lib/api-interface';
import { ContractorData } from './contractor-view/contractor-data/contractor-data';
import { ContractorClientServices } from './contractor-view/contractor-client-services';
import { ContractorDecisionsConclusions } from './contractor-view/contractor-decisions-conclusions/contractor-decisions-conclusions';
import { ClientTabsWrapper } from './tabs-wrapper/client-tabs-wrapper';
import { ContractorTabsWrapper } from './tabs-wrapper/contractor-tabs-wrapper';
import { useQueryClient } from '@tanstack/react-query';
import { DataResultsDto, RestUsecaseClientService } from '@lib/common-sdk';
import { useCanDeleteCompany, useCompanyView } from '@data-source-hooks';
import { LoadingIndicator } from '../../../../libs/ui-components/components/loading-indicator';
import { FetchErrorIndicator } from '../../../../libs/ui-components/components/fetch-error-indicator';
import { DateTimeService } from '../../../../services/date-time.service';
import * as Yup from 'yup';

export const CompanyView = () => {
  const { t } = useTranslation();
  const pathParams = useParams();
  const [tabPages, setTabPages] = React.useState<TabPage[]>([]);
  const [activePage, setActivePage] = React.useState(0);
  const queryClient = useQueryClient();
  const [companyQuery] = useCompanyView(pathParams['companyId']!.substring(1));
  const [canDeleteCompanyQuery] = useCanDeleteCompany(pathParams['companyId']!.substring(1));
  const navigate = useNavigate();
  const [failureMessage, setFailureMessage] = React.useState('');

  function isOnlyClient(model: CompanyViewModel) {
    return model?.roleClient === true && model?.roleContractor === false;
  }

  function isOnlyContractor(model: CompanyViewModel) {
    return model?.roleClient === false && model?.roleContractor === true;
  }

  function isBothClientAndContractor(model: CompanyViewModel) {
    return model?.roleClient === true && model?.roleContractor === true;
  }

  React.useEffect(() => {
    if (companyQuery.isSuccess && canDeleteCompanyQuery.isSuccess) {
      const tabs: TabPage[] = [];
      if (isOnlyClient(companyQuery.data)) {
        tabs.push({
          label: t('Companies.CompanyView.companyClientInfo'),
          component: <ClientData company={companyQuery.data} />,
        });
        tabs.push({
          label: t('branches'),
          component: <CompanyBranches company={companyQuery.data} onCentralChanged={() => companyQuery.refetch()} />,
        });
        tabs.push({
          label: t('Companies.CompanyView.infrastructure'),
          component: <ClientInfrastructure />,
        });
        tabs.push({
          label: t('agreements'),
          component: <CompanyAgreements company={company} role={CompanyRole.CLIENT} />,
        });
      } else if (isOnlyContractor(companyQuery.data)) {
        tabs.push({
          label: t('Companies.CompanyView.companySubcontractorInfo'),
          component: <ContractorData company={companyQuery.data} />,
        });
        tabs.push({ label: t('clientsServices'), component: <ContractorClientServices company={companyQuery.data} /> });
        tabs.push({
          label: t('branches'),
          component: <CompanyBranches company={companyQuery.data} onCentralChanged={() => companyQuery.refetch()} />,
        });
        tabs.push({
          label: t('agreements'),
          component: <CompanyAgreements company={company} role={CompanyRole.SUBCONTRACTOR} />,
        });
        tabs.push({
          label: t('decisionsConclusions'),
          component: <ContractorDecisionsConclusions company={company} />,
        });
      }
      setTabPages(tabs);
    }
  }, [companyQuery.data, companyQuery.isLoading, canDeleteCompanyQuery.isLoading, t]);

  if (companyQuery.isLoading || canDeleteCompanyQuery.isLoading) {
    return <LoadingIndicator />;
  }

  if (companyQuery.error) {
    return <FetchErrorIndicator error={companyQuery.error} />;
  }

  if (canDeleteCompanyQuery.error) {
    return <FetchErrorIndicator error={canDeleteCompanyQuery.error} />;
  }

  const company: CompanyViewModel = companyQuery.data!;

  return (
    <>
      <Panel
        borderType='raised'
        header={company.name}
        direction={'row'}
        rightItem={
          <HStack>
            <EditModelButton
              id={company.id ?? 0}
              entity={company}
              testId={'companies'}
              dialogTitle={t('companyData').toUpperCase()}
              fields={[
                {
                  rowItems: [
                    { field: 'name', label: t('name'), type: 'text' },
                    { field: 'shortName', label: t('companyShortName'), type: 'text' },
                  ],
                },
                {
                  rowItems: [
                    { field: 'vatin', label: t('vatin'), type: 'text' },
                    { field: 'nationalCourtRegister', label: t('nationalCourtRegister'), type: 'text' },
                    { field: 'creditor', label: t('creditor'), type: 'text' },
                    { field: 'debitor', label: t('debitor'), type: 'text' },
                  ],
                },
                {
                  rowItems: [
                    { field: 'nkdClass', label: t('nkdClass'), type: 'text' },
                    { field: 'transportAuthority', label: t('transportAuthority'), type: 'text' },
                    { field: 'authority', label: t('authority'), type: 'text' },
                  ],
                },
                {
                  rowItems: [
                    { field: 'roleClient', label: t('client'), type: 'boolean' },
                    { field: 'roleContractor', label: t('subcontractor'), type: 'boolean' },
                  ],
                },
              ]}
              validationSchema={{
                name: Yup.string().required(t('requiredField')),
                vatin: Yup.string().required(t('requiredField')).max(14, t('fieldToLong')),
                nationalCourtRegister: Yup.string().required(t('requiredField')).max(9, t('fieldToLong')),
                nkdClass: Yup.string().required(t('requiredField')),
                transportAuthority: Yup.string().required(t('requiredField')),
                authority: Yup.string().required(t('requiredField')),
              }}
              additionalFormButtons={[
                <UsecaseButton key='usecase-button-169'
                  useCaseName='UpdateCompanyArchivedStatusUseCase'
                  groupName='company'
                  hiddenValues={{ companyId: company.companyId, archived: company.active }}
                  onError={setFailureMessage}
                  onSaved={async () => await queryClient.invalidateQueries({ queryKey: ['companyViews', company.companyId] })}
                  forceConfirmation={true}
                  buttonLabel={t(company.active ? 'deactivate' : 'reactivate')}
                  color='warning'
                />,
              ]}
              hiddenValues={{ companyId: company.companyId }}
              overrideSaveCall={async (payload: any) => {
                try {
                  await RestUsecaseClientService.post('company', 'update-company-info', payload);
                  return new DataResultsDto(false, 200, undefined, undefined, undefined);
                } catch (error: any) {
                  return new DataResultsDto(true, error.status, undefined, undefined, undefined);
                }
              }}
              onSaved={async () => {
                await queryClient.invalidateQueries({ queryKey: ['companyViews', company.companyId] });
              }}
            />
            <RenderIf true={canDeleteCompanyQuery.data === true}>
              <DeleteOnAggregateButton
                aggregateId={company.companyId!}
                aggregateName='CompanyAggregate'
                commandName='deleteCompany'
                payload={{}}
                onDeleted={() => navigate('/companies')}
              />
            </RenderIf>
          </HStack>
        }
      >
        {/*
          The outer grid is two columns wide (GridLabel's default), so it takes exactly two children: the fields on
          the left and the role checkboxes on the right. Croatia has eight fields rather than the five the Polish
          platform shows, and they used to sit in a second GridLabel passed as a third child - which claimed the right
          column and pushed the checkboxes onto a row of their own, in the middle of the panel. The eight now share
          one left-hand block, five columns wide, so the second row lines up under the first.
        */}
        <GridLabel placeItems='end'>
          <GridLabel gridTemplateColumns='1fr' contentPaddingLeft='0' contentPaddingTop='0' contentPaddingBottom='0'>
            <GridLabel gridTemplateColumns='repeat(5, 1fr)' contentPaddingLeft='0'>
              <TextLabel label={t('companyShortName')} value={company.shortName} />
              <TextLabel label={t('vatin')} value={company.vatin} />
              <TextLabel label={t('nationalCourtRegister')} value={company.nationalCourtRegister} />
              <TextLabel label={t('creditor')} value={company.creditor} />
              <TextLabel label={t('debitor')} value={company.debitor} />
            </GridLabel>
            <GridLabel gridTemplateColumns='repeat(5, 1fr)' contentPaddingLeft='0'>
              <TextLabel label={t('nkdClass')} value={company.nkdClass} />
              <TextLabel label={t('transportAuthority')} value={company.transportAuthority} />
              <TextLabel label={t('authority')} value={company.authority} />
            </GridLabel>
          </GridLabel>

          <Panel direction={'row'}>
            <CheckboxPanel>
              <CheckboxLabel label={t('client')} value={company.roleClient} />
              <CheckboxLabel label={t('subcontractor')} value={company.roleContractor} />
            </CheckboxPanel>
          </Panel>
        </GridLabel>
      </Panel>
      {isBothClientAndContractor(company) && (
        <>
          <Panel contentMarginBottom='2rem' contentMarginTop='2rem'>
            <HStack align='center'>
              <ToggleButtons labels={[t('client'), t('subcontractor')]} activeIndex={activePage} onChange={(index) => setActivePage(index)} />
            </HStack>
          </Panel>
          {activePage === 0 && (
            <ClientTabsWrapper
              company={company}
              onCentralBranchChanged={() => {
                companyQuery.refetch();
              }}
            />
          )}
          {activePage === 1 && (
            <ContractorTabsWrapper
              company={company}
              onCentralBranchChanged={() => {
                companyQuery.refetch();
              }}
            />
          )}
        </>
      )}
      <Tabs marginTop='1rem' panels={tabPages} borderType='none' />
      {company.lastModifiedBy != null && (
        <TextLabel value={`${t('lastModified')}: ${company.lastModifiedBy}  [${DateTimeService.isoStringDateToDateTime(company.lastModifiedDate)}]`} />
      )}
      <RenderIf true={failureMessage !== undefined && failureMessage !== ''}>
        <Snackbar message={failureMessage} severity='error' onClose={() => setFailureMessage('')} />
      </RenderIf>
    </>
  );
};
